import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { useARMClient } from '@wix/arm-client';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useBiLogger } from '@wix/cli-telemetry-react';
import { useHttpClient } from '@wix/cli-http-client-react';
import {
  Text,
  Box,
  PointerIcon,
  useAsync,
  useAsyncCallback,
} from '@wix/cli-ui-kit';
import { useDevCenterClient, ComponentType } from '@wix/dev-center-client';
import type { ComponentsForSiteOverride } from '@wix/site-override-client';
import { useSiteOverrideClient } from '@wix/site-override-client';
import { useProjectModel } from '../../model.js';
import { useSite } from '../../site/index.js';
import { ArmTagProvider } from '../../context/armTag.js';
import { createPreviewTag } from '../../preview/create-preview-tag.js';
import { uploadApplication } from '../../hosting/upload-application.js';
import { assertBuildOutputExists } from '../../files/assert-build-output-exists.js';
import { ActionStatus } from './ActionStatus.js';
import { PlatformViewer } from '../Platform/PlatformViewer.js';
import { saveAppConfig } from '../../files/app.config.js';
import { useBackendComponents } from '../../backend/useBackendComponents.js';
import { createSiteOverrides } from '../../site-override/createSiteOverrides.js';
import { SiteOverrideProvider } from '../../context/siteOverride.js';
import { useBlocksServicesClient } from '../../blocks/useBlocksServicesClient.js';
import { getMonitoringProvider } from '../../app-manifest/monitoring.js';

export interface PreviewData {
  armTag: string;
  siteOverrideId?: string;
}

export const Preview: FC<{
  baseUrl?: string;
  onPreviewCreated?: (data: PreviewData) => void;
}> = ({ baseUrl, onPreviewCreated }) => {
  const { site } = useSite();
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const { backendComponents } = useBackendComponents();
  const arm = useARMClient();
  const biLogger = useBiLogger();
  const { getAppByVersion } = useDevCenterClient();
  const { createOverride } = useSiteOverrideClient();
  const httpClient = useHttpClient({ type: 'backoffice' });
  const blocksServicesClient = useBlocksServicesClient();

  const uploadApplicationAction = useAsyncCallback(() =>
    uploadApplication(
      model.config.appId,
      model.projectFolder,
      biLogger,
      httpClient,
      backendComponents.length > 0
    )
  );

  const createSiteOverrideIdAction = useAsyncCallback(
    (_, overrides: ComponentsForSiteOverride) =>
      createOverride({
        appId: model.config.appId,
        siteId: site.id,
        overrides,
      })
  );

  const createPreviewAction = useAsyncCallback(
    async (_, serveUrl: string, backendServerUrl?: string) => {
      const {
        data: { components },
      } = await getAppByVersion({
        appId: model.config.appId,
      });
      const topologyComponent = components.find((component) => {
        return component.compType === ComponentType.TOPOLOGY;
      });
      const monitoringComponent = components.find((component) => {
        return component.compType === ComponentType.MONITORING;
      });
      const overrides = createSiteOverrides(model);
      const { id: siteOverrideId } = overrides
        ? await createSiteOverrideIdAction.execute(overrides)
        : await Promise.resolve({ id: undefined });

      const blocksComponentsData =
        await blocksServicesClient.prepareBlocksComponentsData(model);

      const monitoringComponentData =
        model.config.monitoring &&
        getMonitoringProvider(model.config.monitoring);

      const armTag = await createPreviewTag({
        model,
        arm,
        serveUrl,
        backendServerUrl,
        blocksComponentsData,
        topologyComponentId: topologyComponent?.compId,
        monitoringData: monitoringComponentData,
        monitoringComponentId: monitoringComponent?.compId,
      });

      if (topologyComponent || monitoringComponent) {
        // Once there is topology/monitoring in production I want to use the same id for dev as well,
        // to reduce confusion during debug.
        await saveAppConfig(model.projectFolder, {
          ...(topologyComponent
            ? { topologyComponentId: topologyComponent.compId }
            : {}),
          ...(monitoringComponent
            ? { monitoringComponentId: monitoringComponent.compId }
            : {}),
        });
      }

      return { armTag, siteOverrideId };
    }
  );

  const { status: createPreviewStatus } = useAsync(async () => {
    await assertBuildOutputExists(model.projectFolder);

    const { serveUrl, backendServerUrl } = baseUrl
      ? { serveUrl: baseUrl, backendServerUrl: undefined }
      : await uploadApplicationAction.execute();

    const data = await createPreviewAction.execute(serveUrl, backendServerUrl);

    onPreviewCreated?.(data);

    return data;
  }, []);

  return (
    <>
      <ActionStatus
        status={uploadApplicationAction.status}
        loadingText={t('preview_command.uploading_files')}
        successText={t('preview_command.uploading_files_success')}
      />
      <ActionStatus
        status={createPreviewAction.status}
        loadingText={t('preview_command.creating_preview')}
        successText={t('preview_command.creating_preview_success')}
      />
      {match(createPreviewStatus, {
        Error: () => null,
        Loading: () => null,
        Success: ({ result: { armTag, siteOverrideId } }) => (
          <SiteOverrideProvider siteOverrideId={siteOverrideId}>
            <ArmTagProvider armTag={armTag}>
              <Box rowGap={1} marginY={1} flexDirection="column">
                <Box gap={1}>
                  <PointerIcon />
                  <Trans
                    i18nKey="general.dev_site.current"
                    values={{ name: site.displayName }}
                  />
                </Box>
                <Box rowGap={1}>
                  <PlatformViewer />
                </Box>
                <Text skin="info">
                  {t('preview_command.note_about_temporary_backend')}
                </Text>
              </Box>
            </ArmTagProvider>
          </SiteOverrideProvider>
        ),
      })}
    </>
  );
};
